// src/tabs/messages/JournalTab.jsx — Onglet Journal (ADR-268).
//
// Poste de pilotage LECTURE SEULE du moteur de messages, sans jargon :
//   - À VENIR    : projection des prochains envois (action serveur
//                  messages_journal_upcoming, horizon 14 j) avec statut
//                  prévisionnel en clair (« Partira », « En attente de la
//                  caution — partira dès réception », …).
//   - HISTORIQUE : message_logs paginé (messages_journal_history), statuts
//                  rendus en clair + note « par rattrapage » dérivée.
//
// Clic sur une ligne (les 2 vues) → JournalPreviewModal : l'aperçu du message
// EXACT pour CETTE résa (moteur de rendu du preview MessageEditor réutilisé).
// L'onglet n'écrit JAMAIS rien : tout est lecture.

import { useState, useEffect, useMemo, useCallback } from "react"
import {
  RefreshCw, AlertTriangle, CalendarClock, ScrollText, Zap, FlaskConical,
  ChevronLeft, ChevronRight, Filter,
} from "lucide-react"
import { supabase } from "../../supabase"
import { DS } from "../../lib/ui"
import { useIsMobile } from "../../lib/useIsMobile"
import { getAllLogsFlat } from "../../lib/sousloc-helpers"
import JournalPreviewModal from "./JournalPreviewModal"

const PAGE_SIZE = 50

// Codes serveur → teinte de badge (le libellé FR vient du serveur, source
// unique testée — l'UI ne reformule pas).
const STATUS_TINTS = {
  will_send:        { bg: DS.successBg, fg: DS.successText },
  catchup_imminent: { bg: DS.successBg, fg: DS.successText },
  on_event:         { bg: DS.cielSoft,  fg: DS.marine },
  conditional:      { bg: DS.warningBg, fg: DS.warningText },
  waiting:          { bg: DS.warningBg, fg: DS.warningText },
  missed_window:    { bg: DS.dangerBg,  fg: DS.dangerText },
  wont_send:        { bg: DS.pageBg,    fg: DS.textMuted },
  // Historique
  sent:             { bg: DS.successBg, fg: DS.successText },
  sent_dry_run:     { bg: DS.cielSoft,  fg: DS.marine },
  failed:           { bg: DS.dangerBg,  fg: DS.dangerText },
  skipped:          { bg: DS.pageBg,    fg: DS.textMuted },
}
const tintFor = (code) => STATUS_TINTS[code] || STATUS_TINTS.skipped

const CANAL_LABELS = {
  tchat_ota: "Chat OTA", email: "Email", notif_interne: "Notif interne",
  whatsapp: "WhatsApp", sms: "SMS", webpush: "Push",
}
const SOURCE_LABELS = { airbnb: "Airbnb", booking: "Booking", direct: "Direct" }

// Formatage jour civil (YYYY-MM-DD) — midi UTC pour éviter tout glissement.
function fmtDay(dateStr) {
  if (!dateStr) return ""
  return new Intl.DateTimeFormat("fr-FR", { weekday: "short", day: "numeric", month: "short" })
    .format(new Date(`${dateStr}T12:00:00Z`))
}
// Formatage d'un instant en HEURE DE PARIS (sent_at timestamptz).
function fmtParis(iso) {
  if (!iso) return "—"
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso))
}
function fmtParisTime(iso) {
  if (!iso) return ""
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso)).replace(":", "h")
}
const fmtHHMM = (t) => {
  const m = String(t || "").match(/^(\d{1,2}):(\d{2})/)
  return m ? `${m[1].padStart(2, "0")}h${m[2]}` : null
}

// Appel action admin (pattern MessageEditor : Bearer JWT session).
async function fetchJournalAction(action, body) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  const resp = await fetch(`/api/beds24-mapping?action=${action}`, {
    method: "POST",
    headers: token
      ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
      : { "Content-Type": "application/json" },
    body: JSON.stringify({ action, ...body }),
  })
  // `json === null` = réponse illisible (page d'erreur, coupure) : on le garde
  // pour distinguer un refus EXPLICITE du serveur d'un résultat inconnu.
  const json = await resp.json().catch(() => null)
  if (!resp.ok) {
    const err = new Error((json && json.error) || `Erreur ${resp.status}`)
    err.status = resp.status
    err.motifServeur = !!(json && typeof json.error === "string" && json.error)
    throw err
  }
  return json || {}
}

export default function JournalTab({ cfg }) {
  const isMobile = useIsMobile()
  const [view, setView] = useState("upcoming") // 'upcoming' | 'history'
  const [preview, setPreview] = useState(null) // { mode, row, message }

  const allLogs = useMemo(() => getAllLogsFlat(cfg), [cfg])
  const logName = useCallback(
    (id) => (allLogs.find(l => l.id === id) || {}).nom || id,
    [allLogs],
  )

  return (
    <div>
      {/* Sélecteur de vue (segmented control, pattern TemplateTab) */}
      <div style={{display:"inline-flex",gap:4,padding:4,marginBottom:14,background:DS.pageBg,borderRadius:DS.radiusControl,border:`0.5px solid ${DS.border}`}}>
        {[["upcoming", "À venir", CalendarClock], ["history", "Historique", ScrollText]].map(([id, label, Icon]) => {
          const active = view === id
          return (
            <button key={id} data-testid={`journal-view-${id}`} onClick={() => setView(id)}
              className="fbz-press"
              style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 16px",border:"none",borderRadius:DS.radiusControl,background:active?DS.marine:"transparent",color:active?"#fff":DS.textSecondary,fontWeight:active?700:600,fontSize:12.5,cursor:"pointer",fontFamily:DS.fontBody,transition:`all ${DS.durBase}`}}>
              <Icon size={14} strokeWidth={2}/> {label}
            </button>
          )
        })}
      </div>

      {view === "upcoming" && (
        <UpcomingView cfg={cfg} isMobile={isMobile} allLogs={allLogs} logName={logName}
          onPreview={(row, message) => setPreview({ mode: "upcoming", row, message })}/>
      )}
      {view === "history" && (
        <HistoryView cfg={cfg} isMobile={isMobile} allLogs={allLogs} logName={logName}
          onPreview={(row, message) => setPreview({ mode: "history", row, message })}/>
      )}

      {preview && (
        <JournalPreviewModal mode={preview.mode} row={preview.row} message={preview.message}
          cfg={cfg} onClose={() => setPreview(null)}/>
      )}
    </div>
  )
}

// Bandeau répétition générale — l'info vient du SERVEUR (payload dry_run),
// jamais d'une lecture client de l'env (garde-fou brief).
function DryRunBanner() {
  return (
    <div data-testid="journal-dryrun-banner"
      style={{display:"flex",gap:9,alignItems:"center",padding:"9px 13px",marginBottom:12,background:DS.cielSoft,borderRadius:10,borderLeft:`3px solid ${DS.marine}`,fontSize:12,color:DS.marine,fontWeight:600}}>
      <FlaskConical size={15} strokeWidth={2} style={{flexShrink:0}}/>
      Répétition générale — le moteur tourne en simulation : les messages sont préparés et tracés, rien n'est réellement envoyé.
    </div>
  )
}

function ErrorBox({ msg }) {
  return (
    <div style={{display:"flex",gap:9,alignItems:"center",padding:"9px 12px",background:DS.dangerBg,color:DS.dangerText,borderRadius:8,borderLeft:`3px solid ${DS.danger}`,fontSize:12.5}}>
      <AlertTriangle size={15} strokeWidth={2} style={{flexShrink:0}}/> {msg}
    </div>
  )
}

function Chip({ children, bg, fg }) {
  return (
    <span style={{fontSize:9.5,fontWeight:700,color:fg||DS.textSecondary,background:bg||DS.pageBg,padding:"2px 8px",borderRadius:6,whiteSpace:"nowrap"}}>
      {children}
    </span>
  )
}

function SelectFilter({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      style={{border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusControl,padding:"7px 9px",fontSize:11.5,background:DS.card,fontFamily:DS.fontBody,color:DS.textPrimary,maxWidth:220}}>
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  )
}

// ── VUE À VENIR ─────────────────────────────────────────────────────

function UpcomingView({ cfg, isMobile, allLogs, logName, onPreview }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [filterSlug, setFilterSlug] = useState("all")
  const [filterLog, setFilterLog] = useState("all")

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setData(await fetchJournalAction("messages_journal_upcoming", {}))
    } catch (e) {
      console.error("[journal] upcoming:", e)
      setError(e.message)
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  const rows = useMemo(() => {
    let r = (data?.rows || []).filter(Boolean)
    if (filterSlug !== "all") r = r.filter(x => x.send.slug === filterSlug)
    if (filterLog !== "all") r = r.filter(x => x.logement_id === filterLog)
    return r
  }, [data, filterSlug, filterLog])

  const messageOptions = useMemo(() => {
    const seen = new Map()
    for (const r of (data?.rows || []).filter(Boolean)) {
      if (r.send.slug && !seen.has(r.send.slug)) seen.set(r.send.slug, r.send.name || r.send.slug)
    }
    return [["all", "Tous les messages"], ...Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))]
  }, [data])

  const eventRows = rows.filter(r => r.kind === "event")
  const temporalRows = rows.filter(r => r.kind !== "event")

  return (
    <div>
      {data?.dry_run && <DryRunBanner/>}

      {/* Filtres + refresh */}
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:12}}>
        <Filter size={14} strokeWidth={2} color={DS.textMuted}/>
        <SelectFilter testid="journal-filter-message" value={filterSlug} onChange={setFilterSlug} options={messageOptions}/>
        <SelectFilter testid="journal-filter-logement" value={filterLog} onChange={setFilterLog}
          options={[["all", "Tous les logements"], ...allLogs.map(l => [l.id, l.nom || l.id])]}/>
        <span style={{flex:1}}/>
        <button className="fbz-press" onClick={load} disabled={loading}
          style={{display:"inline-flex",alignItems:"center",gap:6,background:DS.card,color:DS.marine,border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusControl,padding:"7px 13px",fontSize:11.5,fontWeight:600,cursor:loading?"wait":"pointer",fontFamily:DS.fontBody}}>
          <RefreshCw size={13} strokeWidth={2}/> Actualiser
        </button>
      </div>

      {loading && <div className="fbz-card" style={{padding:14,color:DS.textSecondary,fontSize:12,background:DS.card,borderRadius:DS.radiusCard,border:`0.5px solid ${DS.border}`}}>Chargement…</div>}
      {error && <ErrorBox msg={error}/>}

      {!loading && !error && rows.length === 0 && (
        <div className="fbz-card" style={{padding:"28px 16px",textAlign:"center",background:DS.card,borderRadius:DS.radiusCard,border:`0.5px solid ${DS.border}`}}>
          <CalendarClock size={26} strokeWidth={2} color={DS.marine} style={{marginBottom:8}}/>
          <div style={{fontFamily:DS.fontDisplay,fontSize:15,fontWeight:600,color:DS.marineDark}}>Aucun envoi programmé</div>
          <div style={{fontSize:12,color:DS.textMuted,marginTop:4}}>
            Rien à prévoir sur les {data?.horizon_days ?? 14} prochains jours pour ces critères.
          </div>
        </div>
      )}

      {!loading && !error && eventRows.length > 0 && (
        <SectionCard Icon={Zap} label="Dès qu'un événement se produit"
          desc="Envois déclenchés par un changement (ex. caution reçue) — pas d'heure fixe.">
          {eventRows.map(r => (
            <UpcomingRow key={r.key} row={r} isMobile={isMobile} logName={logName}
              onClick={() => onPreview(r, data?.messages?.[r.send.message_id] || null)}/>
          ))}
        </SectionCard>
      )}

      {!loading && !error && temporalRows.length > 0 && (
        <SectionCard Icon={CalendarClock} label="Prochains envois programmés"
          desc={`Ordre chronologique (heure de Paris) — horizon ${data?.horizon_days ?? 14} jours.`}>
          {temporalRows.map(r => (
            <UpcomingRow key={r.key} row={r} isMobile={isMobile} logName={logName}
              onClick={() => onPreview(r, data?.messages?.[r.send.message_id] || null)}/>
          ))}
        </SectionCard>
      )}
    </div>
  )
}

function SectionCard({ Icon, label, desc, children }) {
  return (
    <div className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:16,marginBottom:14,border:`0.5px solid ${DS.border}`}}>
      <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:11,paddingBottom:9,borderBottom:`1px solid ${DS.border}`,flexWrap:"wrap"}}>
        <span style={{width:28,height:28,borderRadius:8,background:DS.cielSoft,color:DS.marine,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <Icon size={15} strokeWidth={2}/>
        </span>
        <span style={{fontFamily:DS.fontDisplay,fontSize:14,fontWeight:600,color:DS.marineDark}}>{label}</span>
        <span style={{fontSize:10.5,color:DS.textMuted,flex:"1 1 auto"}}>{desc}</span>
      </div>
      {children}
    </div>
  )
}

function UpcomingRow({ row, isMobile, logName, onClick }) {
  const tint = tintFor(row.status.code)
  const r = row.reservation || {}
  const guest = `${r.guest_first_name || "?"} ${r.guest_last_name || ""}`.trim()
  return (
    <div className="fbz-rowhover" data-testid="journal-upcoming-row" onClick={onClick}
      style={{display:"flex",alignItems:isMobile?"flex-start":"center",gap:isMobile?6:12,padding:"10px 12px",border:`0.5px solid ${DS.border}`,borderRadius:10,marginBottom:7,cursor:"pointer",background:DS.card,flexDirection:isMobile?"column":"row"}}>
      {/* Quand (heure Paris) */}
      <div style={{width:isMobile?"auto":110,flexShrink:0}}>
        {row.occurrence ? (
          <>
            <div style={{fontSize:12.5,fontWeight:700,color:DS.marineDark,fontFamily:DS.fontDisplay}}>{fmtDay(row.occurrence.date)}</div>
            <div style={{fontSize:11,color:DS.textSecondary,fontWeight:600}}>{fmtHHMM(row.occurrence.time) || "dans la journée"}</div>
          </>
        ) : (
          <div style={{fontSize:11.5,fontWeight:700,color:DS.marine}}>À l'événement</div>
        )}
      </div>
      {/* Quoi + pour qui */}
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:2}}>
          <span style={{fontSize:13,fontWeight:600,color:DS.marineDark}}>{row.send.name || row.send.slug}</span>
          <Chip bg={DS.cielSoft} fg={DS.marine}>{CANAL_LABELS[row.send.canal] || row.send.canal}</Chip>
          {(row.send.sources || []).map(s => <Chip key={s}>{SOURCE_LABELS[s] || s}</Chip>)}
          {row.audience === "admin" && <Chip bg={DS.catArdoiseBg} fg={DS.marineDark}>Pour moi</Chip>}
        </div>
        <div style={{fontSize:11.5,color:DS.textSecondary}}>
          {guest || "?"} · {logName(row.logement_id)} · {fmtDay(r.start_date)} → {fmtDay(r.end_date)}
        </div>
      </div>
      {/* Statut prévisionnel (libellé serveur, source unique) */}
      <div style={{flexShrink:0,maxWidth:isMobile?"100%":300}}>
        <span data-testid="journal-upcoming-status" style={{display:"inline-block",fontSize:11,fontWeight:700,color:tint.fg,background:tint.bg,padding:"4px 10px",borderRadius:9,lineHeight:1.4}}>
          {row.status.label}
        </span>
      </div>
    </div>
  )
}

// ── VUE HISTORIQUE ──────────────────────────────────────────────────

function HistoryView({ cfg, isMobile, allLogs, logName, onPreview }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [offset, setOffset] = useState(0)
  const [filterSlug, setFilterSlug] = useState("all")
  const [filterLog, setFilterLog] = useState("all")
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  // Liste des messages pour le select (slug + nom humain) — lecture directe
  // (pattern MessagesTab), indépendante des pages du journal.
  const [messageOptions, setMessageOptions] = useState([["all", "Tous les messages"]])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data: msgs, error: e } = await supabase.from("messages").select("slug, name").order("name")
      if (e) { console.error("[journal] messages list:", e); return }
      if (!cancelled && msgs) {
        setMessageOptions([["all", "Tous les messages"], ...msgs.map(m => [m.slug, m.name || m.slug])])
      }
    })()
    return () => { cancelled = true }
  }, [])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const body = { limit: PAGE_SIZE, offset }
      if (filterSlug !== "all") body.template_slug = filterSlug
      if (filterLog !== "all") body.logement_id = filterLog
      if (from) body.from = from
      if (to) body.to = to
      setData(await fetchJournalAction("messages_journal_history", body))
    } catch (e) {
      console.error("[journal] history:", e)
      setError(e.message)
    } finally { setLoading(false) }
  }, [offset, filterSlug, filterLog, from, to])
  useEffect(() => { load() }, [load])

  const rows = (data?.rows || []).filter(Boolean)
  const total = data?.total || 0
  const dateInput = {border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusControl,padding:"6px 8px",fontSize:11.5,background:DS.card,fontFamily:DS.fontBody,color:DS.textPrimary}

  return (
    <div>
      {data?.dry_run && <DryRunBanner/>}

      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:12}}>
        <Filter size={14} strokeWidth={2} color={DS.textMuted}/>
        <SelectFilter testid="journal-hist-filter-message" value={filterSlug}
          onChange={v => { setOffset(0); setFilterSlug(v) }} options={messageOptions}/>
        <SelectFilter testid="journal-hist-filter-logement" value={filterLog}
          onChange={v => { setOffset(0); setFilterLog(v) }}
          options={[["all", "Tous les logements"], ...allLogs.map(l => [l.id, l.nom || l.id])]}/>
        <label style={{fontSize:10.5,color:DS.textMuted,display:"inline-flex",alignItems:"center",gap:5}}>
          du <input type="date" value={from} onChange={e => { setOffset(0); setFrom(e.target.value) }} style={dateInput}/>
        </label>
        <label style={{fontSize:10.5,color:DS.textMuted,display:"inline-flex",alignItems:"center",gap:5}}>
          au <input type="date" value={to} onChange={e => { setOffset(0); setTo(e.target.value) }} style={dateInput}/>
        </label>
        <span style={{flex:1}}/>
        <button className="fbz-press" onClick={load} disabled={loading}
          style={{display:"inline-flex",alignItems:"center",gap:6,background:DS.card,color:DS.marine,border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusControl,padding:"7px 13px",fontSize:11.5,fontWeight:600,cursor:loading?"wait":"pointer",fontFamily:DS.fontBody}}>
          <RefreshCw size={13} strokeWidth={2}/> Actualiser
        </button>
      </div>

      {loading && <div className="fbz-card" style={{padding:14,color:DS.textSecondary,fontSize:12,background:DS.card,borderRadius:DS.radiusCard,border:`0.5px solid ${DS.border}`}}>Chargement…</div>}
      {error && <ErrorBox msg={error}/>}

      {!loading && !error && rows.length === 0 && (
        <div className="fbz-card" style={{padding:"28px 16px",textAlign:"center",background:DS.card,borderRadius:DS.radiusCard,border:`0.5px solid ${DS.border}`}}>
          <ScrollText size={26} strokeWidth={2} color={DS.marine} style={{marginBottom:8}}/>
          <div style={{fontFamily:DS.fontDisplay,fontSize:15,fontWeight:600,color:DS.marineDark}}>Aucune trace pour ces critères</div>
          <div style={{fontSize:12,color:DS.textMuted,marginTop:4,maxWidth:460,margin:"4px auto 0"}}>
            Le moteur écrit une trace à chaque envoi (réel ou simulé) et à chaque blocage.
            S'il ne s'est encore rien passé, c'est normal que cette liste soit vide.
          </div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:16,marginBottom:14,border:`0.5px solid ${DS.border}`}}>
          {rows.map(row => (
            <HistoryRow key={row.id} row={row} isMobile={isMobile} logName={logName}
              onClick={() => onPreview(row, data?.messages?.[row.message_id] || null)}
              onRenvoye={load}/>
          ))}
        </div>
      )}

      {!loading && !error && total > PAGE_SIZE && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:14}}>
          <button className="fbz-press" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            style={{display:"inline-flex",alignItems:"center",gap:5,background:DS.card,color:offset===0?DS.textMuted:DS.marine,border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusControl,padding:"6px 12px",fontSize:11.5,fontWeight:600,cursor:offset===0?"default":"pointer",fontFamily:DS.fontBody}}>
            <ChevronLeft size={14} strokeWidth={2}/> Précédent
          </button>
          <span style={{fontSize:11,color:DS.textSecondary}}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} sur {total}
          </span>
          <button className="fbz-press" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}
            style={{display:"inline-flex",alignItems:"center",gap:5,background:DS.card,color:offset+PAGE_SIZE>=total?DS.textMuted:DS.marine,border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusControl,padding:"6px 12px",fontSize:11.5,fontWeight:600,cursor:offset+PAGE_SIZE>=total?"default":"pointer",fontFamily:DS.fontBody}}>
            Suivant <ChevronRight size={14} strokeWidth={2}/>
          </button>
        </div>
      )}
    </div>
  )
}

// RENVOYER D'UN GESTE (29/09, condition Dimitri) : un message automatique en
// échec se renvoie d'ici, tel qu'il devait partir, par le même canal. Deux
// clics (le second confirme : c'est un envoi RÉEL au voyageur). Le serveur
// verrouille — un message ne se renvoie qu'une fois — et le résultat dit ce
// qui s'est vraiment passé : jamais « renvoyé » sans que ce soit parti.
export function peutRenvoyer(row) {
  if (!row || row.status !== "failed") return false
  // Le serveur a jugé (dernière trace du couple, pas reparti depuis) : il fait foi.
  if (row.renvoyable === false) return false
  const m = row.metadata || {}
  // Une marque « en cours » de plus de 10 min sans issue = renvoi INTERROMPU
  // (jugé par le serveur) : elle ne bloque plus le bouton.
  if (m.renvoye_le || (m.renvoi_en_cours_le && m.renvoi_interrompu !== true)) return false
  return row.canal === "tchat_ota" || row.canal === "email"
}

// ⚠️ UN ÉCHEC DU CLIC N'EST PAS FORCÉMENT « NON PARTI » (revue 29/09). Si la
// fonction meurt APRÈS que Beds24 a reçu le message (envoi lent, délai
// dépassé), l'écran ne reçoit qu'une coupure, une page d'erreur ou un 5xx.
// Écrire « Non parti » invitait à recliquer — double envoi. Seul un REFUS
// EXPLICITE du serveur (4xx avec son motif : verrou, mode essai, déjà
// renvoyé…) dit que rien n'est parti ; tout le reste est un résultat INCONNU.
export const MSG_RESULTAT_INCONNU =
  "Résultat inconnu : le message est peut-être parti. Vérifie la conversation avant de renvoyer."
export function libelleEchecRenvoi(err) {
  const st = err && Number(err.status)
  if (Number.isFinite(st) && st >= 400 && st < 500 && err.motifServeur === true) {
    return { ok: false, inconnu: false, msg: `Non parti : ${err.message}` }
  }
  return { ok: false, inconnu: true, msg: MSG_RESULTAT_INCONNU }
}

// Un renvoi précédent lancé et jamais terminé (jugé par le serveur) : le
// bouton se rouvre, mais JAMAIS sans le dire.
export const MSG_RENVOI_INTERROMPU =
  "Un renvoi a été lancé et ne s'est jamais terminé : le voyageur l'a peut-être déjà reçu. Vérifie la conversation avant de renvoyer."
export function renvoiInterrompuSurLigne(row) {
  return !!(row && row.status === "failed" && row.metadata && row.metadata.renvoi_interrompu === true)
}

export function BoutonRenvoyer({ row, onRenvoye }) {
  const [etat, setEtat] = useState(null) // null | 'confirmer' | 'envoi' | {ok,msg}
  if (!peutRenvoyer(row) && !(etat && etat.ok !== undefined)) return null
  const clic = async (e) => {
    e.stopPropagation()
    if (etat === null) { setEtat("confirmer"); return }
    if (etat !== "confirmer") return
    setEtat("envoi")
    try {
      await fetchJournalAction("messages_journal_renvoyer", { log_id: row.id })
      setEtat({ ok: true, msg: "Renvoyé — parti réellement." })
      onRenvoye && onRenvoye()
    } catch (err) {
      setEtat(libelleEchecRenvoi(err))
    }
  }
  const interrompu = renvoiInterrompuSurLigne(row)
  if (etat && etat.ok !== undefined) {
    return (
      <div data-testid="journal-renvoi-resultat" onClick={(e) => e.stopPropagation()}
        style={{fontSize:11,fontWeight:700,marginTop:6,color:etat.ok?DS.successText:DS.dangerText}}>{etat.msg}</div>
    )
  }
  return (
    <>
      {etat === "confirmer" && interrompu && (
        <div data-testid="journal-renvoi-confirmer-avertissement" onClick={(e) => e.stopPropagation()}
          style={{fontSize:10.5,fontWeight:700,color:DS.dangerText,marginTop:6,maxWidth:280}}>
          {MSG_RENVOI_INTERROMPU}
        </div>
      )}
      <button type="button" data-testid="journal-renvoyer" className="fbz-press" onClick={clic}
        disabled={etat === "envoi"}
        style={{marginTop:6,fontSize:11,fontWeight:700,border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"4px 10px",
          background:etat==="confirmer"?DS.dangerBg:DS.card,color:etat==="confirmer"?DS.dangerText:DS.marine,cursor:"pointer"}}>
        {etat === "envoi"
          ? "Renvoi…"
          : etat === "confirmer"
            ? interrompu
              ? "Confirmer : renvoyer quand même (il l'a peut-être déjà reçu)"
              : "Confirmer : renvoyer au voyageur"
            : "Renvoyer"}
      </button>
    </>
  )
}

export function HistoryRow({ row, isMobile, logName, onClick, onRenvoye }) {
  const tint = tintFor(row.display.code)
  const r = row.reservation || null
  const guest = r ? `${r.guest_first_name || "?"} ${r.guest_last_name || ""}`.trim() : null
  return (
    <div className="fbz-rowhover" data-testid="journal-history-row" onClick={onClick}
      style={{display:"flex",alignItems:isMobile?"flex-start":"center",gap:isMobile?6:12,padding:"10px 12px",border:`0.5px solid ${DS.border}`,borderRadius:10,marginBottom:7,cursor:"pointer",background:DS.card,flexDirection:isMobile?"column":"row"}}>
      <div style={{width:isMobile?"auto":110,flexShrink:0}}>
        <div style={{fontSize:12,fontWeight:700,color:DS.marineDark,fontFamily:DS.fontDisplay}}>{fmtParis(row.sent_at)}</div>
        <div style={{fontSize:10,color:DS.textMuted}}>heure de Paris</div>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:2}}>
          <span style={{fontSize:13,fontWeight:600,color:DS.marineDark}}>{row.message_name || row.template_slug}</span>
          <Chip bg={DS.cielSoft} fg={DS.marine}>{CANAL_LABELS[row.canal] || row.canal}</Chip>
          {(row.send?.sources || []).map(s => <Chip key={s}>{SOURCE_LABELS[s] || s}</Chip>)}
        </div>
        <div style={{fontSize:11.5,color:DS.textSecondary}}>
          {guest ? `${guest} · ` : ""}{row.logement_id ? logName(row.logement_id) : ""}
          {r ? ` · ${fmtDay(r.start_date)} → ${fmtDay(r.end_date)}` : ""}
        </div>
        {row.catchup && (
          <div style={{fontSize:10.5,color:DS.warningText,marginTop:3}}>
            Envoyé à {fmtParisTime(row.sent_at)} par rattrapage
            {row.catchup.planned_time ? ` (prévu ${row.catchup.planned_date ? `le ${fmtDay(row.catchup.planned_date)} ` : ""}à ${row.catchup.planned_time})` : ""}
          </div>
        )}
        {row.display.detail && (
          <div style={{fontSize:10.5,color:DS.dangerText,marginTop:3,fontFamily:"monospace"}}>{row.display.detail}</div>
        )}
      </div>
      <div style={{flexShrink:0,maxWidth:isMobile?"100%":300}}>
        <span data-testid="journal-history-status" style={{display:"inline-block",fontSize:11,fontWeight:700,color:tint.fg,background:tint.bg,padding:"4px 10px",borderRadius:9,lineHeight:1.4}}>
          {row.display.label}
        </span>
        {row.metadata && row.metadata.envoi_incertain === true && row.status === "failed" && (
          <div data-testid="journal-incertain" style={{fontSize:10.5,color:DS.warningText,marginTop:4,maxWidth:280}}>
            Envoi incertain : Beds24 a peut-être reçu le message. Vérifie la conversation avant de renvoyer.
          </div>
        )}
        {renvoiInterrompuSurLigne(row) && (
          <div data-testid="journal-renvoi-interrompu" style={{fontSize:10.5,color:DS.warningText,marginTop:4,maxWidth:280}}>
            {MSG_RENVOI_INTERROMPU}
          </div>
        )}
        <BoutonRenvoyer row={row} onRenvoye={onRenvoye}/>
      </div>
    </div>
  )
}
